/**
 * GlobalErrorHandler - Manages common HTTP errors consistently across the application
 * Enhanced version with feedback system and sophisticated error handling
 */

import { HttpError } from './HttpService';
import dialogUtil from '../../shared/utils/dialog';
import LocalStorageService from './LocalStorageService';

export class GlobalErrorHandler {
  private static instance: GlobalErrorHandler;

  static getInstance(): GlobalErrorHandler {
    if (!GlobalErrorHandler.instance) {
      GlobalErrorHandler.instance = new GlobalErrorHandler();
    }
    return GlobalErrorHandler.instance;
  }

  /**
   * Handle common HTTP errors with consistent UX
   * Returns true if error was handled, false if component should handle it
   */
  handle(error: HttpError): boolean {
    switch (error.status) {
      case 401:
        this.handleUnauthorized(error);
        return true;

      case 402:
        return true;  // Handled by axios interceptor (billing)

      case 403:
        if (['quota_exceeded', 'feature_not_enabled'].includes(error.data?.error)) {
          return true;  // Handled by axios interceptor (billing)
        }
        this.handleForbidden(error);
        return true;

      case 404:
        this.handleNotFound(error);
        return true;

      case 500:
        this.handleServerError(error);
        return true;

      case 502:
      case 503:
        this.handleServerError(error);
        return true;

      case 504:
        this.handleGatewayTimeout(error);
        return true;

      default:
        // Let domain handlers deal with business logic errors (400, 409, etc.)
        return false;
    }
  }

  /**
   * Fallback for completely unhandled errors
   */
  handleUnexpected(error: any): void {
    console.error('Unexpected error:', error);
    
    dialogUtil.showModal({
      title: 'An unexpected error occurred',
      content: error.data ? JSON.stringify(error.data) : 'No additional information available',
      primaryAction: {
        label: 'Provide Feedback',
        onPress: () => this.provideFeedback(error)
      },
      variant: 'error',
      closeOnBackdropPress: true,
    });
  }

  // Private error handling methods
  private handleUnauthorized(error: HttpError): void {
    console.log('Authentication required. Redirecting to login...');

    dialogUtil.showToast({
      title: 'Unauthorized',
      message: 'Please login to continue',
      type: 'warning'
    });

    // Clear any stored auth data
    LocalStorageService.clearAuthData();

    // Redirect to login after a brief delay
    setTimeout(() => {
      window.location.href = '/login';
    }, 1500);
  }

  private handleForbidden(error: HttpError): void {
    console.log('Access forbidden:', error.data);
    
    const errorMessage = this.extractErrorMessage(error);
    
    dialogUtil.showToast({
      title: 'Forbidden',
      message: errorMessage,
      type: 'error',
    });
    
    dialogUtil.showModal({
      title: 'Access Denied',
      content: errorMessage,
      primaryAction: {
        label: 'Provide Feedback',
        onPress: () => this.provideFeedback(error),
      },
      variant: 'warning',
      closeOnBackdropPress: true,
    });
  }

  private handleNotFound(error: HttpError): void {
    console.log('Resource not found:', error.data);
    
    const errorMessage = this.extractErrorMessage(error);
    
    dialogUtil.showToast({
      title: 'Not Found',
      message: errorMessage,
      type: 'error',
    });
    
    dialogUtil.showModal({
      title: 'Resource Not Found',
      content: errorMessage,
      primaryAction: {
        label: 'Provide Feedback',
        onPress: () => this.provideFeedback(error),
      },
      variant: 'warning',
      closeOnBackdropPress: true,
    });
  }

  private handleServerError(error: HttpError): void {
    console.error('Server error occurred:', error.message, error.data);
    
    const errorMessage = this.extractErrorMessage(error);
    const isServerDown = error.status === 502 || error.status === 503;
    const title = isServerDown ? 'Service Unavailable' : 'Server Error';
    
    dialogUtil.showToast({
      title: title,
      message: errorMessage,
      type: 'error',
    });
    
    dialogUtil.showModal({
      title: title,
      content: errorMessage,
      primaryAction: {
        label: 'Provide Feedback',
        onPress: () => this.provideFeedback(error),
      },
      variant: 'error',
      closeOnBackdropPress: true,
    });
  }

  private handleGatewayTimeout(error: HttpError): void {
    console.error('Gateway timeout:', error.data);
    
    const errorMessage = this.extractErrorMessage(error);
    
    dialogUtil.showToast({
      title: 'Gateway Timeout',
      message: errorMessage,
      type: 'error',
    });
    
    dialogUtil.showModal({
      title: 'Request Timeout',
      content: `The request took too long to complete. ${errorMessage}`,
      primaryAction: {
        label: 'Provide Feedback',
        onPress: () => this.provideFeedback(error),
      },
      variant: 'warning',
      closeOnBackdropPress: true,
    });
  }

  /**
   * Extract meaningful error message from error response
   */
  private extractErrorMessage(error: HttpError): string {
    if (error.data?.detail) {
      return error.data.detail;
    }
    
    if (error.data && typeof error.data === 'object') {
      return JSON.stringify(error.data);
    }
    
    return 'No additional information available';
  }

  /**
   * Opens a feedback form for users to report issues
   * For now uses a simple prompt, but can be enhanced with proper form components
   */
  private provideFeedback(error: any): void {
    // Use the dialog utility's feedback modal
    dialogUtil.showFeedbackModal(error);
  }
}

// Export singleton instance
const globalErrorHandler = GlobalErrorHandler.getInstance();
export default globalErrorHandler; 
